"use client";

import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  CheckUserIdResponse,
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  UserProfile,
} from "@growing/types";
import { createAuthApi, AuthApiConfig } from "./index";

/**
 * Auth hooks factory
 * client와 config를 받아서 hooks를 생성합니다.
 */
export function createAuthHooks(client: AxiosInstance, config: AuthApiConfig) {
  const authApi = createAuthApi(client, config);

  return {
    /**
     * 아이디 중복 확인 Hook
     */
    usePostCheckUserId: (
      options?: UseMutationOptions<CheckUserIdResponse, Error, string>
    ) => {
      return useMutation<CheckUserIdResponse, Error, string>({
        mutationFn: authApi.getCheckUserId,
        ...options,
      });
    },

    /**
     * 회원가입 Hook
     */
    usePostSignup: (
      options?: UseMutationOptions<SignupResponse, Error, SignupRequest>
    ) => {
      return useMutation<SignupResponse, Error, SignupRequest>({
        mutationFn: authApi.postSignup,
        ...options,
      });
    },

    /**
     * 로그인 Hook
     */
    usePostLogin: (
      options?: UseMutationOptions<LoginResponse, Error, LoginRequest>
    ) => {
      return useMutation<LoginResponse, Error, LoginRequest>({
        mutationFn: authApi.postLogin,
        ...options,
      });
    },

    /**
     * 사용자 프로필 조회 Hook
     */
    useGetProfile: (
      options?: Omit<UseQueryOptions<UserProfile, Error>, "queryKey" | "queryFn">
    ) => {
      return useQuery<UserProfile, Error>({
        queryKey: ["auth", "profile"],
        queryFn: authApi.getMyProfile,
        ...options,
      });
    },
  };
}
